import React, { useEffect, useRef, useState } from 'react'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'

// Phase 2b: simple MediaRecorder UI with three states (idle → recording →
// preview). Transcription happens server-side via TranscribeResponseJob, so
// this component just hands the parent an audio Blob to upload.
//
// Recording length is capped at MAX_AUDIO_MS. Pairs with MAX_AUDIO_BYTES in
// app/controllers/api/v1/responses_controller.rb — change both together to
// raise the limit (e.g. 5 min → 10 min). No schema or Whisper config impact.
const MAX_AUDIO_MS = 5 * 60 * 1000 // 5 minutes

interface AudioRecorderProps {
  onChange: (blob: Blob | null) => void
  disabled?: boolean
}

export default function AudioRecorder({ onChange, disabled = false }: AudioRecorderProps) {
  const recorder = useAudioRecorder()
  const isRecording = recorder.isRecording
  const audioBlob = recorder.audioBlob
  const audioUrl = recorder.audioUrl

  // Keep mutable refs to the recorder controls so the timer effect can read
  // the latest functions without subscribing to them in its deps array.
  const stopRecordingRef = useRef(recorder.stopRecording)
  useEffect(() => {
    stopRecordingRef.current = recorder.stopRecording
  }, [recorder.stopRecording])

  const [elapsedMs, setElapsedMs] = useState(0)

  // Notify the parent whenever the captured blob changes.
  useEffect(() => {
    onChange(audioBlob ?? null)
  }, [audioBlob, onChange])

  // Tick a 5×/sec timer while recording. Depends ONLY on isRecording (a
  // boolean primitive), so the effect runs exactly twice per recording
  // session: once when isRecording flips true, once when it flips false.
  useEffect(() => {
    if (!isRecording) {
      return undefined
    }

    const startedAt = Date.now()
    setElapsedMs(0)

    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - startedAt
      setElapsedMs(elapsed)
      if (elapsed >= MAX_AUDIO_MS) {
        stopRecordingRef.current?.()
      }
    }, 200)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [isRecording])

  const handleStart = async () => {
    await recorder.startRecording()
  }

  const handleStop = () => {
    recorder.stopRecording()
  }

  const handleReRecord = () => {
    recorder.reset()
    setElapsedMs(0)
  }

  // ---- idle ----
  if (!recorder.audioBlob && !recorder.isRecording) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-slate-200 bg-white p-6">
        <button
          type="button"
          onClick={handleStart}
          disabled={disabled}
          className="group relative h-20 w-20 rounded-full bg-accent-600 shadow-lg shadow-accent-200 transition hover:bg-accent-500 active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-300"
          aria-label="Start recording"
        >
          <span className="absolute inset-0 flex items-center justify-center">
            <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3z" />
              <path d="M19 11a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.92V20H8a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2h-3v-2.08A7 7 0 0 0 19 11z" />
            </svg>
          </span>
        </button>
        <p className="text-sm text-slate-500">
          Tap to record your response (max {formatMs(MAX_AUDIO_MS)})
        </p>
        {recorder.error && <p className="text-sm text-rose-600">{recorder.error}</p>}
      </div>
    )
  }

  // ---- recording ----
  if (recorder.isRecording) {
    const remainingMs = Math.max(0, MAX_AUDIO_MS - elapsedMs)
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-rose-200 bg-rose-50 p-6">
        <button
          type="button"
          onClick={handleStop}
          className="relative h-20 w-20 rounded-full bg-rose-600 shadow-lg shadow-rose-200 transition hover:bg-rose-500 active:scale-95"
          aria-label="Stop recording"
        >
          <span className="absolute inset-0 animate-ping rounded-full bg-rose-400 opacity-50" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-6 w-6 rounded-sm bg-white" />
          </span>
        </button>
        <p className="text-sm font-medium text-rose-700">
          Recording — tap to stop ({formatMs(remainingMs)} remaining)
        </p>
      </div>
    )
  }

  // ---- preview ----
  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-white p-4">
      {recorder.audioUrl && <audio src={recorder.audioUrl} controls className="w-full" />}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleReRecord}
          disabled={disabled}
          className="text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-40"
        >
          ↺ Re-record
        </button>
        <span className="text-xs text-slate-500">
          {formatMs(elapsedMs)} recorded
        </span>
      </div>
    </div>
  )
}

function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
